/**
 * LES CHEMINS DE L'APPLICATION — declares en UN SEUL endroit.
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ UN CHEMIN ECRIT A LA MAIN DANS UN COMPOSANT EST UNE ERREUR DE DEMAIN. ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * Renommer /espaces en /organisations demanderait alors de chercher des
 * chaines dans tout le code, et celle qu'on oublierait ne se verrait qu'au
 * clic d'un utilisateur.
 *
 * ── LES CHEMINS D'INTERFACE NE SONT PAS CEUX DE L'API ────────────────────
 *
 * L'API expose /api/v1/locataires/{code}/projets/{projet}/automatisations.
 * L'interface montre /espaces/{code}/projets/{projet}/automatisations. Le mot « locataire » est une notion de
 * modele -- il ne doit pas remonter jusqu'a l'ecran, ou personne ne se pense
 * comme « locataire ».
 */

export const CHEMINS = {
  connexion: '/connexion',
  espaces: '/espaces',
  /** MONDE PLATEFORME [28-BU] : actes transversaux, autorises par
   *  role_plateforme et non par appartenance. Distinct de /espaces, qui
   *  repond a « de quels espaces suis-je membre ». Un operateur n est pas
   *  membre des clients qu il cree. */
  plateforme: '/plateforme',
  nouveauClient: '/plateforme/clients/nouveau',
  /** LE NIVEAU 2 DU COCKPIT [29-I] : un client, ses projets, et les gestes
   *  d etat de sa portee. La liste de /plateforme reste en LECTURE SEULE --
   *  des boutons destructifs sur chaque carte d un inventaire invitent au
   *  clic de travers. */
  clientPlateforme: (idClient: string): string =>
    `/plateforme/clients/${encodeURIComponent(idClient)}`,
  espace: (code: string): string => `/espaces/${encodeURIComponent(code)}`,
  /** La liste des projets d un espace. Niveau CLIENT [contrat 28-BQ]. */
  projets: (code: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets`,
  /** Creer un projet dans cet espace. Niveau CLIENT. */
  nouveauProjet: (code: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/nouveau`,
  /** Un projet, et ce qu il contient. Niveau PROJET. */
  projet: (code: string, projet: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/${encodeURIComponent(projet)}`,
  automatisations: (code: string, projet: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/${encodeURIComponent(projet)}`
    + `/automatisations`,
  /** Les environnements du projet. Rubrique ACTIVE des 29-D [28-CH]. */
  environnements: (code: string, projet: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/${encodeURIComponent(projet)}`
    + `/environnements`,
  /** Les executions du PROJET. A distinguer de `executions`, qui vise le
   *  niveau ESPACE : deux vues de rangs differents, deux routes moteur
   *  distinctes. Le suffixe dit le niveau, comme pour les autres. */
  executionsProjet: (code: string, projet: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/${encodeURIComponent(projet)}`
    + `/executions`,
  /** Les parametres du projet. Rubrique ACTIVE des 29-F : nom et description
   *  y sont editables, le code y est AFFICHE et jamais modifiable. */
  parametres: (code: string, projet: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/${encodeURIComponent(projet)}`
    + `/parametres`,
  /**
   * L EDITEUR, EN PLEINE PAGE ET HORS DE LA COQUILLE.
   *
   * Un canevas a besoin de toute la largeur : la barre laterale d espace
   * lui prendrait 240 pixels en permanence, pour une navigation dont on ne
   * se sert pas quand on dessine un graphe. C est le choix de n8n comme de
   * Make -- et il se defait en deplacant une ligne si l usage dit autre
   * chose.
   *
   * L identifiant est celui de l API : un id PUBLIC, jamais l entier
   * interne. Le moteur refuse d ailleurs les deux formes autrement.
   */
  /** 29-E — le formulaire de creation. Chemin FIXE sous le projet, donc
   *  une fonction de ses deux segments et non une constante. */
  nouvelleAutomatisation: (code: string, projet: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/${encodeURIComponent(projet)}`
    + `/automatisations/nouvelle`,
  editeur: (code: string, projet: string, id: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/${encodeURIComponent(projet)}`
    + `/automatisations/${encodeURIComponent(id)}/editeur`,
  /** 28-AI — la vue serveur de comparaison, ouverte lors d un conflit.
   *  Une capacite d interface : « representation en lecture seule du
   *  brouillon serveur courant ». Pas encore une politique de role. */
  brouillonLecture: (code: string, projet: string, id: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/${encodeURIComponent(projet)}`
    + `/automatisations/${encodeURIComponent(id)}/brouillon/lecture`,
  /** 29-G ph.3 — LES VERSIONS D UNE AUTOMATISATION, et leur deploiement.
   *  DANS la coquille, contrairement a l editeur et au brouillon en lecture
   *  seule : ceux-la sont en pleine page parce qu un CANEVAS a besoin de
   *  toute la largeur. Une liste n a pas ce besoin, et la navigation du
   *  projet doit rester accessible depuis un ecran de consultation.
   *  Le domicile est l AUTOMATISATION : les versions lui appartiennent. */
  versions: (code: string, projet: string, id: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/${encodeURIComponent(projet)}`
    + `/automatisations/${encodeURIComponent(id)}/versions`,
  /* LA CONFIGURATION D UN ENVIRONNEMENT [29-H]. Le domicile est
   *  l ENVIRONNEMENT, et il se designe par sa NATURE : le couple
   *  (projet, nature) est l identite publique [28-CC]. Un seul contexte
   *  actif a l ecran -- TEST ou PRODUCTION, jamais les deux cote a cote,
   *  parce qu on y ecrit des secrets. */
  configurationEnvironnement: (code: string, projet: string, nature: string): string =>
    `/espaces/${encodeURIComponent(code)}/projets/${encodeURIComponent(projet)}`
    + `/environnements/${encodeURIComponent(nature)}/configuration`,
  executions: (code: string): string =>
    `/espaces/${encodeURIComponent(code)}/executions`,
  validations: (code: string): string =>
    `/espaces/${encodeURIComponent(code)}/validations`,
  cles: (code: string): string => `/espaces/${encodeURIComponent(code)}/cles`,
  equipe: (code: string): string => `/espaces/${encodeURIComponent(code)}/equipe`,
} as const;

/**
 * LA NAVIGATION D'UN ESPACE.
 *
 * Chaque entree correspond a une famille de routes REELLE de l'API -- rien
 * n'est promis qui n'existe pas cote serveur. `pret` distingue ce qui est
 * construit de ce qui viendra : un lien grise DIT que la rubrique existera,
 * la ou son absence laisserait croire qu'elle n'est pas prevue.
 */
export interface EntreeNavigation {
  readonly cle: string;
  readonly libelle: string;
  readonly chemin: (code: string) => string;
  readonly pret: boolean;
  /** La capacite que l'API exigera. Sert a MASQUER, jamais a autoriser. */
  readonly capaciteMinimale: readonly string[];
}

export const NAVIGATION: readonly EntreeNavigation[] = [
  // AUTOMATISATIONS N EST PLUS ICI [contrat 28-BQ]. Cette barre est celle du
  // NIVEAU CLIENT ; une automatisation appartient a un PROJET. Les melanger
  // reconstruirait dans l interface la confusion que 28-BH a supprimee du
  // modele. On passe donc par Projets, et la liste vit dans le projet.
  { cle: 'projets', libelle: 'Projets',
    chemin: CHEMINS.projets, pret: true,
    capaciteMinimale: ['lecteur', 'editeur', 'administrateur', 'proprietaire'] },
  { cle: 'executions', libelle: 'Exécutions',
    chemin: CHEMINS.executions, pret: false,
    capaciteMinimale: ['lecteur', 'editeur', 'administrateur', 'proprietaire'] },
  { cle: 'validations', libelle: 'Validations',
    chemin: CHEMINS.validations, pret: false,
    // `validation.rendre` commence a l'editeur : un lecteur ne voit pas
    // cette rubrique. L'API refuserait de toute facon -- on evite juste de
    // proposer une porte fermee.
    capaciteMinimale: ['editeur', 'administrateur', 'proprietaire'] },
  { cle: 'equipe', libelle: 'Équipe',
    chemin: CHEMINS.equipe, pret: false,
    capaciteMinimale: ['lecteur', 'editeur', 'administrateur', 'proprietaire'] },
  { cle: 'cles', libelle: 'Clés d’API',
    chemin: CHEMINS.cles, pret: false,
    // `cle_api.gerer` est au PROPRIETAIRE seul.
    capaciteMinimale: ['proprietaire'] },
];

/**
 * Ce qu'un role peut VOIR dans la navigation.
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ CECI MASQUE. CELA N'AUTORISE PAS.                                     ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * L'API tranche seule, et refuserait de toute facon. Ne pas proposer une
 * rubrique qu'on ne peut pas ouvrir est une question de courtoisie, pas de
 * securite -- un controle cote navigateur se contourne avec un outil de
 * developpement.
 */
export function entreesVisibles(role: string): readonly EntreeNavigation[] {
  return NAVIGATION.filter((e) => e.capaciteMinimale.includes(role));
}
